import React, { useContext } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useUi } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";

import { messages } from "./Mixing.messages";
import styles from "./Mixing.module.scss";

import { Beds } from "./components/Beds";
import { Panning } from "./components/Panning";
import { Reverbs } from "./components/Reverbs";
import { Dialogue } from "./components/Dialogue";
import { Loudness } from "./components/Loudness";
import { LFE } from "./components/LFE";
import { Speakers } from "./components/Speakers";
import { Height } from "./components/Height";

interface Mixing {
  title: string;
}

export const Mixing: React.FC<Mixing> = ({ title }) => {
  return (
    <Accordion allowMultiOpen={false} defaultOpen={["beds"]}>
      <MixingContent title={title} />
    </Accordion>
  );
};

const MixingContent: React.FC<Mixing> = ({ title }) => {
  const ui = useUi();
  const { formatMessage } = useIntl();
  const context = useContext(AccordionContext);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem id={"beds"} title={formatMessage(messages.accordionItem1Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem1Description)}</p>
        </AccordionItem>
        <AccordionItem id={"panning"} title={formatMessage(messages.accordionItem2Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem2Description)}</p>
        </AccordionItem>
        <AccordionItem id={"height"} title={formatMessage(messages.accordionItem3Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem3Description)}</p>
        </AccordionItem>
        <AccordionItem id={"speakers"} title={formatMessage(messages.accordionItem4Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem4Description)}</p>
        </AccordionItem>
        <AccordionItem id={"lfe"} title={formatMessage(messages.accordionItem5Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem5Description)}</p>
        </AccordionItem>
        <AccordionItem id={"loudness"} title={formatMessage(messages.accordionItem6Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem6Description)}</p>
        </AccordionItem>
        <AccordionItem id={"dialogue"} title={formatMessage(messages.accordionItem7Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem7Description)}</p>
        </AccordionItem>
        <AccordionItem id={"reverb"} title={formatMessage(messages.accordionItem8Title)} unmountChildrenWhenHidden>
          <p>{formatMessage(messages.accordionItem8Description)}</p>
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  const renderRightComponent = (): React.ReactNode => {
    if (context.openIds.includes("beds")) {
      return <Beds />;
    }
    if (context.openIds.includes("panning")) {
      return <Panning />;
    }
    if (context.openIds.includes("reverb")) {
      return <Reverbs />;
    }
    if (context.openIds.includes("dialogue")) {
      return <Dialogue />;
    }
    if (context.openIds.includes("loudness")) {
      return <Loudness />;
    }
    if (context.openIds.includes("lfe")) {
      return <LFE />;
    }
    if (context.openIds.includes("speakers")) {
      return <Speakers />;
    }
    if (context.openIds.includes("height")) {
      return <Height />;
    }
    return null;
  };

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.rightComponent}>{renderRightComponent()}</div>
    </StandardCase>
  );
};
